import React, { useEffect, useState } from 'react';
import { useField } from 'formik';

import {
    error,
    button,
    list,
    listElement,
    listValue,
    inputContainer,
    label as labelClass,
    dropdown,
    dropdownList,
    dropdownItem,
} from './multi-input.module.scss';

import CloseIcon from '../../assets/images/svg/close.svg';

import Error from './error';
import Input from './input';
import IconButton from './icon-button';

export interface IMultiInputProps {
    className?: string;
    name: string;
    id?: string;
    hint?: string;
    label?: string;
    isDisabled?: boolean;
    isRequired?: boolean;
    identifier?: string;
    placeholder?: string;
    predefinedOptions?: string[];
}

const EMAIL_REGEX_WITHOUT_SPACE = /^[\p{L}0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/u;
const EMAIL_REGEX_WITH_SPACE = /^[\p{L}0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\s$/u;

const MultiInput: React.FC<IMultiInputProps> = ({
    className = '',
    name,
    hint,
    identifier,
    label,
    isRequired,
    id,
    isDisabled,
    placeholder,
    predefinedOptions = [],
}) => {
    const [, meta, helpers] = useField(name);
    const [fieldValues, setFieldValues] = useState<string[]>([]);
    const [filteredOptions, setFilteredOptions] = useState<string[]>(predefinedOptions);
    const [inputValue, setInputValue] = useState('');

    const handleChange = (value: string, validateWithSpace = false) => {
        setInputValue(value);
        setFilteredOptions(
            predefinedOptions
                .filter((option) => !fieldValues.includes(option))
                .filter((email) => email.includes(value))
        );

        if (
            (validateWithSpace && EMAIL_REGEX_WITH_SPACE.test(value)) ||
            (!validateWithSpace && EMAIL_REGEX_WITHOUT_SPACE.test(value))
        ) {
            const newValues = [...fieldValues, value.replace(' ', '')];

            setFieldValues(newValues);
            helpers.setValue(newValues.join(','));
            setInputValue('');
        }
    };

    const handleDelete = (value: string) => {
        return () => {
            const index = fieldValues.indexOf(value);
            const newValues = fieldValues.filter((_, i) => i !== index);
            setFieldValues(newValues);
            helpers.setValue(newValues.join(','));
            setInputValue('');
        };
    };

    const handleAddOption = (option: string) => {
        return () => {
            handleChange(option);
            setInputValue('');
        };
    };

    useEffect(() => {
        if (fieldValues.length === 0 && meta.value && meta.value !== '') {
            helpers.setValue(meta.value);
            setFieldValues(meta.value.split(','));
        }
    }, [fieldValues, helpers, meta.value]);

    useEffect(() => {
        if (fieldValues && fieldValues.length) {
            setFilteredOptions(predefinedOptions.filter((option) => !fieldValues.includes(option)));
        } else {
            setFilteredOptions(predefinedOptions);
        }
    }, [fieldValues, predefinedOptions]);

    return (
        <div className={className}>
            <div className={labelClass}>{label}</div>
            <div className={inputContainer}>
                <ul className={list}>
                    {fieldValues.map((value, index) => {
                        return (
                            <li key={`multi-input-value-${index}`} className={listElement}>
                                <p className={listValue}>{value}</p>
                                {!isDisabled && (
                                    <IconButton
                                        type="button"
                                        onClick={handleDelete(value)}
                                        className={button}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                )}
                            </li>
                        );
                    })}
                    <Input
                        id={id}
                        name={name}
                        showError={false}
                        disabled={isDisabled}
                        identifier={identifier}
                        placeholder={placeholder}
                        isRequired={isRequired}
                        hint={hint}
                        value={inputValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleChange(e.target.value, true)
                        }
                        onBlur={() => {
                            handleChange(inputValue);
                        }}
                    />
                </ul>
                {filteredOptions.length > 0 && (
                    <div className={dropdown}>
                        <div className={dropdownList}>
                            {filteredOptions.map((option, index) => (
                                <button
                                    className={dropdownItem}
                                    key={`user-option-${index}`}
                                    type={'button'}
                                    onClick={handleAddOption(option)}
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <Error name={name} className={error} />
        </div>
    );
};

export default MultiInput;
